<template>
  <v-dialog
    v-model="localDialog"
    width="60%"
    @update:modelValue="updateDialog"
    transition=""
    persistent
    :open-delay="0"
    :close-delay="0"
  >
    <base-card
      :title="isEntryMode ? 'Add Bank Account' : 'Update Bank Account'"
      :v-card-text-class="'pa-4'"
    >
<v-form @submit.prevent="submit" ref="LcBankSetup">
          <v-row>
            <v-col lg="6" md="6" cols="12">
              <v-text-field
                density="compact" 
                variant="outlined"
                bg-color="grey-lighten-4"
                v-model="name.value.value"
                :error-messages="name.errorMessage.value"
                clearable
              >
                <template #label>
                  Bank Name <span class="text-red-accent-4">*</span>
                </template>
              </v-text-field>

              <v-text-field
                label="Branch"
                density="compact"
                variant="outlined"
                bg-color="grey-lighten-4"
                v-model="branch.value.value"
                :error-messages="branch.errorMessage.value"
                clearable
              ></v-text-field>

              <v-text-field
                label="SWIFT Code"
                density="compact"
                variant="outlined"
                bg-color="grey-lighten-4"
                v-model="swift_code.value.value"
                :error-messages="swift_code.errorMessage.value"
                clearable
              ></v-text-field>

              <v-textarea
                label="Address (Optional)"
                density="compact"
                variant="outlined"
                bg-color="grey-lighten-4"
                v-model="address.value.value"
                :error-messages="address.errorMessage.value"
                clearable
                auto-grow
                rows="2"
              ></v-textarea>

              <v-checkbox
                label="Active"
                v-model="status.value.value"
                :error-messages="status.errorMessage.value"
                density="compact"
                variant="outlined"
                hide-details
                single-line
                color="primary"
                true-value="1"
                false-value="0"
              ></v-checkbox>
            </v-col>

            <v-col lg="6" md="6" cols="12">
              <v-text-field
                label="Account Name"
                density="compact"
                variant="outlined"
                bg-color="grey-lighten-4"
                v-model="account_name.value.value"
                :error-messages="account_name.errorMessage.value"
                clearable
              ></v-text-field>

              <v-text-field
                label="Account No"
                density="compact"
                variant="outlined"
                bg-color="grey-lighten-4"
                v-model="account_no.value.value"
                :error-messages="account_no.errorMessage.value"
                clearable
              ></v-text-field>

              <v-text-field
                label="Routing No"
                density="compact"
                variant="outlined"
                bg-color="grey-lighten-4"
                v-model="routing_no.value.value"
                :error-messages="routing_no.errorMessage.value"
                clearable
              ></v-text-field>

              <v-textarea
                label="Remarks (Optional)"
                density="compact"
                variant="outlined"
                bg-color="grey-lighten-4"
                v-model="remarks.value.value"
                :error-messages="remarks.errorMessage.value"
                clearable
                auto-grow
                rows="2"
              ></v-textarea>
            </v-col>

            <input v-model="bank_id.value.value" type="hidden" />
          </v-row>

          <v-divider></v-divider>
          <v-card-actions>
            <v-layout class="justify-end">
              <v-btn
                class="bg-red"
                variant="flat"
                type="reset"
                @click="closeDialog"
                >Close</v-btn
              >
              <v-btn class="bg-brand w-15 ms-2" variant="flat" type="submit">
                {{ isEntryMode ? "Save" : "Update" }}
              </v-btn>
            </v-layout>
          </v-card-actions>
        </v-form>
      </base-card>
  </v-dialog>
</template>

<script>
import baseCard from '@/components/utils/baseCard.vue';
import { ref } from "vue";
import { useStore } from "vuex";
import { useField, useForm } from "vee-validate";
import * as yup from "yup";
import { toast } from "vue3-toastify";

export default {
  components: { baseCard },
  name: "LcBankSetup",
  props: {
    dialog: {
      type: Boolean,
      default: false,
    },
    data: {
      type: Object,
      default: () => ({
        name: "",
        branch: "",
        account_name: "",
        account_no: "",
        routing_no: "",
        swift_code: "",
        address: "",
        remarks: "",
        status: "1",
        bank_id: null,
      }),
    },
  },
  data() {
    return {
      localDialog: this.dialog,
      errorMessages: "",
      loading: false,
      isEntryMode: true,
    };
  },
  computed: {},
  watch: {
    dialog(val) {
      this.localDialog = val;
    },
    localDialog(val) {
      this.$emit("update:dialog", val);
    },
    data: {
      handler(newData) {
        this.isEntryMode = !newData.id;
        this.resetForm({ errors: {}, touched: {} });
        this.name.setValue(newData.name || "", false);
        this.branch.setValue(newData.branch || "", false);
        this.account_name.setValue(newData.account_name || "", false);
        this.account_no.setValue(newData.account_no || "", false);
        this.routing_no.setValue(newData.routing_no || "", false);
        this.swift_code.setValue(newData.swift_code || "", false);
        this.address.setValue(newData.address || "", false);
        this.remarks.setValue(newData.remarks || "", false);
        this.status.setValue(String(newData.status) || "1", false);
        this.bank_id.setValue(newData.id || "", false);
      },
    },
  },
  created() {},
  methods: {
    updateDialog(val) {
      this.$emit("update:dialog", val);
    },
    closeDialog() {
      this.$emit("update:dialog", false);
    },
  },
  setup(props, { emit }) {
    const { handleSubmit, resetForm } = useForm({
      validationSchema: yup.object({
        name: yup.string().min(1).max(255).required().trim().label("Bank Name"),
        branch: yup.string().max(255).required().trim().label("Branch"),
        account_no: yup.string().max(255).required().trim().label("Account No."),
        account_name: yup.string().max(255).required().trim().label("Account Name"),
        swift_code: yup.string().max(255).nullable().trim().label("SWIFT Code"),
        address: yup.string().max(255).nullable().trim().label("Address"),
        remarks: yup.string().max(255).nullable().trim().label("Remarks"),
      }),
    });

    const name = useField("name");
    const branch = useField("branch");
    const account_name = useField("account_name");
    const account_no = useField("account_no");
    const routing_no = useField("routing_no");
    const swift_code = useField("swift_code");
    const address = useField("address");
    const remarks = useField("remarks");
    const status = useField("status");
    const bank_id = useField("bank_id");
    const errorMessages = ref("");
    const loading = ref(false);
    const store = useStore();

    const submit = handleSubmit((values) => {
      store
        .dispatch("saveUpdateLcBank", values)
        .then((response) => {
          resetForm();
          emit("update:dialog", false);
          toast.success(response.data.message);
          loading.value = true;
          store
            .dispatch("getLcBankList")
            .then((data) => {
              loading.value = false;
            })
            .catch((error) => {
              loading.value = false;
              toast.error(error.response?.data?.message || "Failed to reload list");
            });
        })
        .catch((error) => {
          toast.error(error.response?.data?.message || "Failed to save bank");
        });
    });

    return {
      submit,
      name,
      branch,
      account_name,
      account_no,
      routing_no,
      swift_code,
      address,
      remarks,
      status,
      bank_id,
      errorMessages,
      resetForm,
      loading,
    };
  },
  mounted() {},
};
</script>

<style scoped>
.bg-brand {
  background-color: #1976d2;
  color: white;
}
</style>

